// R2 — Library, as a shelf of printed editions. Each cover is the entry point
// for its book: pressing one opens a sheet holding that edition's title page
// and table of contents (dotted leaders, Rockwell numerals, unnumbered italic
// entries for front/back matter). The Odyssey is one catalog book but three
// bound volumes, so the shelf is keyed on `ShelfEdition`, not on the catalog —
// see `shelf.ts` for why that mapping lives in the presentation layer.
//
// Chapter entries render `number + title` generically — the catalog title is
// always the chapter's real prose title (e.g. "A Welcomed Guest"), locked
// chapters included, since the paywall discloses titles and gates only text
// (SPEC "Paywall"); nothing here assumes any particular format regardless.

import { Suspense, useRef, useState, type CSSProperties } from "react";
import { flushSync } from "react-dom";
import { Link } from "react-router";
import { useQuery } from "convex/react";
import { Check, Layers, Lock, X } from "lucide-react";
import { Skeleton } from "@bindery/ui/components/ui/skeleton";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@bindery/ui/components/ui/sheet";
import { ErrorBoundary } from "@/components/error-boundary";
import { api } from "@bindery/convex/_generated/api";
import { retryCorpusFetch } from "@/lib/corpus";
import type { CatalogBook } from "@bindery/shared/corpus/catalog";
import { BOOK_CONFIGS } from "@bindery/convex/model/bookConfig";
import { cn } from "@/lib/utils";
import { signIn } from "@/lib/auth";
import { latestPosition } from "./bookmarks";
import { useCoarseNow } from "@/lib/use-coarse-now";
import { useLastLoaded } from "@/lib/use-last-loaded";
import { PriceAmount, SaleMark } from "./sale-price";
import { useBuyProduct } from "./use-buy-product";
import { useBundleOffer } from "./use-bundle-offer";
import { unitPath, unitSegment, type ReadingUnit } from "./reading-order";
import {
  FORTHCOMING,
  groupIntoSeries,
  lastChapterOf,
  shelveCatalog,
  sinkRead,
  type ForthcomingEdition,
  type ShelfEdition,
  type ShelvedEdition,
  type ShelvedSeries,
} from "./shelf";
import { useReadMarks, type ReadMarks } from "./read-marks";
import { useCatalog } from "./use-corpus";

/** Human label for a source-language code; falls back to the raw code. */
const LANGUAGE_LABEL: Record<string, string> = {
  grc: "Ancient Greek",
  en: "English",
  es: "Spanish",
  de: "German",
  fr: "French",
  it: "Italian",
  zh: "Chinese",
};

/** One table-of-contents row: numeral (chapters only), title, dotted leader. */
function ContentsRow({
  bookSlug,
  unit,
}: {
  bookSlug: string;
  unit: ReadingUnit;
}) {
  const isChapter = unit.kind === "chapter";
  // A locked (paywalled) unit is still listed and still linked — the link
  // lands on the upsell (plan 10 §4). It shows a persistent lock in place of
  // the hover arrow so the end of the free run reads as a natural threshold.
  //
  // MATTER CAN BE LOCKED TOO, since a volume's epilogue may be paywalled with
  // its chapters. It keeps its italic, numeral-less styling — the lock says
  // what it costs, the italic still says what KIND of page it is, and the two
  // facts are independent.
  const isLocked = unit.premium;
  return (
    <li>
      <Link
        to={unitPath(bookSlug, unit)}
        className="group focus-visible:ring-ring/60 flex items-baseline gap-3 rounded-sm px-2 py-[0.45rem] focus-visible:ring-2 focus-visible:outline-none"
      >
        {/*
          Full-strength `muted-foreground`, not a fraction of it: over
          `--background` the token is 4.77:1 in light theme and 7.68:1 in
          dark, and the /80 this used to carry knocked the light figure to
          3.25:1 — under the 4.5:1 that 14px non-bold body text needs (WCAG
          SC 1.4.3). The numeral still sits back from the title, which is
          near-black `foreground`, without the alpha.
        */}
        <span
          className={cn(
            "w-7 shrink-0 text-right font-rockwell text-sm tabular-nums",
            isChapter ? "text-muted-foreground" : "text-transparent",
          )}
          aria-hidden="true"
        >
          {isChapter ? unit.number : "·"}
        </span>
        <span
          className={cn(
            "font-serif text-[1.05rem] leading-snug transition-colors group-hover:text-primary",
            !isChapter && "italic",
            isLocked ? "text-muted-foreground" : "text-foreground",
          )}
        >
          {unit.title}
        </span>
        {/* Dotted leader, as on a printed contents page. */}
        <span
          aria-hidden="true"
          className="border-border/80 group-hover:border-foreground/40 mx-1 flex-1 self-end border-b border-dotted pb-[0.3rem] transition-colors"
        />
        {isLocked ? (
          // The lock carries state, not decoration, so it needs 3:1 against
          // the background (WCAG SC 1.4.11); at /70 it was 2.73:1 in light
          // theme. Full `muted-foreground` is 4.77:1 light / 7.68:1 dark.
          <Lock
            aria-label="Locked — unlock to read"
            className="text-muted-foreground group-hover:text-primary size-3.5 self-center transition-colors"
          />
        ) : (
          <span
            aria-hidden="true"
            className="text-muted-foreground/0 group-hover:text-muted-foreground self-center font-serif text-xs transition-colors"
          >
            →
          </span>
        )}
      </Link>
    </li>
  );
}

/**
 * The unlock line (plan 10 §4): shown when the OPEN edition holds locked
 * chapters and the viewer isn't entitled. Price + name come live from
 * `getProduct`; the entitlement query hides the line once owned. Both queries
 * degrade gracefully — an anonymous reader (Convex unauthenticated) simply sees
 * the offer. Links to the first locked chapter, which renders the full upsell.
 *
 * `chapter`'s own `premiumProductId` decides what this buys — a VOLUME's
 * product, once a book is priced per volume, never the whole-title id
 * (`model/volume.ts`'s `productIdForChapter`, threaded onto the catalog).
 * Label follows suit: "this volume" when the edition carries a
 * `volumeLabel`, "this book" for a single-volume title.
 */
function UnlockLine({
  book,
  edition,
  chapter,
}: {
  book: CatalogBook;
  edition: ShelfEdition;
  chapter: Extract<ReadingUnit, { kind: "chapter" }>;
}) {
  const productId = chapter.premiumProductId;
  // Coarse so the query's subscription key is stable (`useCoarseNow`). Two
  // surfaces need the clock: the sale window, and an all-access subscription,
  // which entitles only until it lapses.
  const now = useCoarseNow();
  // Held across the minute tick, for the same reason the price below is (and
  // the reader's own gate — `PremiumChapterGate`): `now` is a query argument,
  // so every tick re-subscribes and `status` is `undefined` for a round trip.
  // Since `status?.entitled` is the ONLY thing suppressing this CTA, an
  // unheld tick offers a reader the book they already own, once a minute,
  // with the layout shift that comes with it. Safe here without a `key`: an
  // `EditionTile` is keyed on `edition.id` by every list that renders one, so
  // the subject cannot change under this instance.
  const status = useLastLoaded(
    useQuery(
      api.checkout.entitlementStatus,
      productId
        ? {
            productId,
            bookSlug: book.slug,
            // The first locked chapter — the one this line is offering to
            // unlock, and the one a volume-scoped comp would be issued for.
            chapterNumber: chapter.number,
            now,
          }
        : "skip",
    ),
  );
  // Held across the minute tick — see `premium-gate`.
  const product = useLastLoaded(
    useQuery(api.checkout.getProduct, productId ? { productId, now } : "skip"),
  );
  if (!productId) return null;
  if (status?.entitled) return null; // already owns this volume's paid chapters

  const label = edition.volumeLabel ? "Unlock this volume" : "Unlock this book";

  return (
    <span className="inline-flex flex-col items-start gap-1">
      {product != null && <SaleMark offer={product} now={now} />}
      <Link
        to={`/read/${book.slug}/${String(chapter.number)}`}
        className="group text-muted-foreground hover:text-primary inline-flex items-center gap-2 font-serif text-sm transition-colors"
      >
        <Lock aria-hidden="true" className="size-3.5" />
        <span className="tracking-wide">
          {label}
          {product != null && (
            <>
              {" — "}
              <PriceAmount
                offer={product}
                className="group-hover:text-primary"
              />
            </>
          )}
        </span>
      </Link>
    </span>
  );
}

/**
 * The complete-title bundle CTA (plan 10 §7 / §5b): restores the "buy it all
 * in one purchase" convenience the over-grant bug used to give away for
 * free, now correctly scoped as its own purchase. Only rendered when the
 * config actually names a bundle product (`legacyBundleProductId`) — a
 * multi-volume title with no bundle minted yet gets no invented CTA, and a
 * single-volume book (no `volumes` at all) never has one.
 */
function BundleUnlockLine({ book, next }: { book: CatalogBook; next: string }) {
  const now = useCoarseNow();
  // Id, price, and the "N of M volumes ready now" line, resolved by the same
  // hook the paywall plate's bundle CTA uses (`use-bundle-offer.ts`).
  const { bundleProductId, product, availabilityNote } = useBundleOffer(
    book,
    now,
  );
  // The shelf's own question, and deliberately NOT part of the shared hook: it
  // renders for a reader who may already own the bundle, while the plate only
  // renders for one who owns nothing that opens this chapter.
  const status = useLastLoaded(
    useQuery(
      api.checkout.entitlementStatus,
      bundleProductId ? { productId: bundleProductId, now } : "skip",
    ),
  );
  const { buy, busy } = useBuyProduct(next);

  if (!bundleProductId) return null;
  if (status?.entitled) return null; // already owns the complete title

  const onClick = (): void => {
    if (!status?.authed) {
      signIn();
      return;
    }
    void buy(bundleProductId);
  };

  return (
    <span className="mt-2 inline-flex flex-col items-start gap-1">
      {product != null && <SaleMark offer={product} now={now} />}
      <button
        type="button"
        onClick={onClick}
        disabled={busy}
        className="group text-muted-foreground hover:text-primary inline-flex items-center gap-2 font-serif text-sm transition-colors disabled:opacity-50"
      >
        <Layers aria-hidden="true" className="size-3.5" />
        <span className="tracking-wide">
          Unlock every volume of {book.title} at once
          {product != null && (
            <>
              {" — "}
              <PriceAmount
                offer={product}
                className="group-hover:text-primary"
              />
            </>
          )}
        </span>
      </button>
      {availabilityNote && (
        <span className="text-muted-foreground font-serif text-xs">
          {availabilityNote}
        </span>
      )}
    </span>
  );
}

/**
 * The cover itself, rendered as a physical object: spine gutter down the left
 * edge, page block on the right, a sheen across the boards, and a contact
 * shadow that spreads as the book lifts. Shared by shelved and forthcoming
 * covers, so a book that isn't retold yet still sits on the same shelf.
 */
function Cover({
  src,
  size,
  alt,
  eager,
  className,
}: {
  src: string;
  size: { width: number; height: number };
  /**
   * Empty when adjacent text already names the cover — inside a button the
   * caption is part of the control's accessible name, so a described alt is
   * read out twice ("Cover — Mentor and the Staff Mentor and the Staff …").
   */
  alt: string;
  eager: boolean;
  className?: string;
}) {
  return (
    <span
      className={cn(
        "relative block overflow-hidden rounded-[3px] rounded-l-[2px]",
        "shadow-[0_10px_18px_-12px_rgba(0,0,0,0.55),0_2px_5px_-3px_rgba(0,0,0,0.4)]",
        // The lift is `motion-safe:` at the call site rather than reversed
        // here, so a reduced-motion reader gets the depth cue without the
        // travel: the shadow still deepens, the book just doesn't move.
        "transition-[transform,box-shadow] duration-300 ease-out motion-reduce:transition-none",
        className,
      )}
    >
      <img
        src={src}
        alt={alt}
        width={size.width}
        height={size.height}
        loading={eager ? "eager" : "lazy"}
        decoding="async"
        className="block h-auto w-full"
      />
      {/* Spine gutter: the shadowed inch of board next to the binding. */}
      <span
        aria-hidden="true"
        className="pointer-events-none absolute inset-y-0 left-0 w-[7%] bg-gradient-to-r from-black/45 via-black/15 to-transparent"
      />
      {/* Page block — the fore-edge of the leaves, catching the light. */}
      <span
        aria-hidden="true"
        className="pointer-events-none absolute inset-y-[1.5%] right-0 w-[1.5%] bg-gradient-to-l from-white/45 to-white/0"
      />
      {/* Sheen across the boards. */}
      <span
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 bg-gradient-to-br from-white/10 via-transparent to-black/10"
      />
      <span
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 rounded-[3px] ring-1 ring-black/20 ring-inset"
      />
    </span>
  );
}

/** The hairline the books stand on, with the cover's contact shadow. */
function ShelfLine() {
  return (
    <span aria-hidden="true" className="relative mt-4 block">
      <span className="bg-border block h-px w-full" />
      <span className="absolute -top-1 left-[8%] h-2 w-[84%] rounded-[50%] bg-black/12 blur-[5px] transition-opacity duration-300 group-hover:opacity-70 motion-reduce:transition-none dark:bg-black/40" />
    </span>
  );
}

/** Caption under a cover: printed title, then the edition's provenance line. */
function CoverCaption({
  title,
  detail,
  muted,
  read,
}: {
  title: string;
  detail: string;
  muted?: boolean;
  /** Finished (`read-marks.ts`) — stamped here, and sunk to the back of its set. */
  read?: boolean;
}) {
  return (
    <span className="mt-4 block">
      <span
        className={cn(
          "block font-display text-lg leading-tight font-bold tracking-tight text-balance transition-colors",
          muted
            ? "text-muted-foreground"
            : "text-foreground group-hover:text-primary",
        )}
      >
        {title}
      </span>
      <span className="text-muted-foreground mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 font-serif text-[0.7rem] tracking-[0.18em] uppercase">
        <span>{detail}</span>
        {read && (
          // Real text, not an icon alone: "read" is state a screen reader has
          // to hear, and it is also what explains why this cover moved to the
          // back of its set.
          <span className="border-border text-foreground/75 inline-flex items-center gap-1 rounded-full border px-1.5 py-px tracking-[0.14em]">
            <Check aria-hidden="true" className="size-3" />
            Read
          </span>
        )}
      </span>
    </span>
  );
}

/** "Books 5–12" / "14 chapters" — what the reader gets inside this cover. */
function editionDetail(book: CatalogBook, edition: ShelfEdition): string {
  const noun = BOOK_CONFIGS[book.slug]?.retellingHeadingNoun ?? "Chapter";
  // A whole-book cover already prints the book's title, so the caption spends
  // its line on provenance instead; a volume's cover title is its OWN ("The
  // Cyclops' Curse"), so there the caption has to name the parent work.
  if (!edition.chapters) {
    const count = book.chapters.length;
    const language = LANGUAGE_LABEL[book.sourceLanguage] ?? book.sourceLanguage;
    return `${language} · ${String(count)} ${noun.toLowerCase()}${count === 1 ? "" : "s"}`;
  }
  const { from, to } = edition.chapters;
  const span = from === to ? String(from) : `${String(from)}–${String(to)}`;
  return `${book.title} · ${noun}s ${span}`;
}

/**
 * One shelved edition: the cover is the sheet trigger, and the sheet is that
 * edition's title page + contents. Contents are computed per edition, so the
 * Odyssey's three volumes each list only the rhapsodies they bind.
 */
function EditionTile({
  book,
  edition,
  units,
  eager,
  read = false,
}: {
  book: CatalogBook;
  edition: ShelfEdition;
  units: readonly ReadingUnit[];
  eager: boolean;
  read?: boolean;
}) {
  const language = LANGUAGE_LABEL[book.sourceLanguage] ?? book.sourceLanguage;
  const noun = (
    BOOK_CONFIGS[book.slug]?.retellingHeadingNoun ?? "Chapter"
  ).toLowerCase();
  const first = units[0];
  const firstLocked = units.find((u) => u.kind === "chapter" && u.premium);

  // Read once per mount: localStorage position records (R7a) drive "Continue
  // reading". A useState initializer keeps the read out of re-renders. The
  // saved chapter only counts for the volume that actually contains it.
  const [saved] = useState(() => latestPosition(book.slug));
  const savedUnit = saved
    ? units.find(
        (u) => u.kind === "chapter" && u.number === saved.chapterNumber,
      )
    : undefined;
  const resume = savedUnit ?? first;

  return (
    <Sheet>
      <SheetTrigger asChild>
        <button
          type="button"
          className="group focus-visible:ring-ring/60 focus-visible:ring-offset-background block cursor-pointer rounded-sm text-left focus-visible:ring-2 focus-visible:ring-offset-4 focus-visible:outline-none"
        >
          {/*
            Decorative here: the caption below is inside the button, so it
            already names the control. An alt would be concatenated onto it.
          */}
          <Cover
            src={edition.cover}
            size={edition.coverSize}
            alt=""
            eager={eager}
            className="group-hover:shadow-[0_22px_34px_-16px_rgba(0,0,0,0.6),0_4px_10px_-4px_rgba(0,0,0,0.45)] motion-safe:group-hover:-translate-y-1.5"
          />
          <ShelfLine />
          <CoverCaption
            title={edition.coverTitle}
            detail={editionDetail(book, edition)}
            read={read}
          />
        </button>
      </SheetTrigger>

      {/* Radix wires SheetContent's aria-describedby to SheetDescription. */}
      <SheetContent side="right" className="w-full gap-0 p-0 sm:max-w-lg">
        {/* Title page */}
        {/* pr-12 keeps the title clear of the sheet's absolute close button. */}
        <SheetHeader className="border-border shrink-0 gap-0 border-b px-7 pt-8 pr-12 pb-6">
          <p className="text-muted-foreground font-serif text-[0.7rem] tracking-[0.3em] uppercase">
            {edition.volumeLabel
              ? `${book.title} · ${edition.volumeLabel}`
              : `Retold from the ${language}`}
          </p>
          <SheetTitle className="font-display mt-2 text-3xl leading-tight font-black tracking-tight text-balance">
            {edition.coverTitle}
          </SheetTitle>
          <SheetDescription className="mt-3 font-serif text-sm">
            {edition.chapters
              ? `${noun.charAt(0).toUpperCase() + noun.slice(1)}s ${String(edition.chapters.from)}–${String(edition.chapters.to)}, retold from the ${language} and set beside the original.`
              : `In ${String(book.chapters.length)} ${noun}${book.chapters.length === 1 ? "" : "s"}, retold side by side with the original.`}
          </SheetDescription>
          {resume && (
            <div className="mt-6 flex flex-wrap items-baseline gap-x-5 gap-y-2">
              <Link
                to={unitPath(book.slug, resume)}
                className="border-foreground text-foreground hover:border-primary hover:text-primary border-b pb-1 font-serif text-sm tracking-[0.2em] uppercase transition-colors"
              >
                {savedUnit ? "Continue reading" : "Begin reading"}
              </Link>
              {savedUnit?.kind === "chapter" && (
                <span className="text-muted-foreground font-serif text-sm italic">
                  {noun.charAt(0).toUpperCase() + noun.slice(1)}{" "}
                  {savedUnit.number}
                </span>
              )}
            </div>
          )}
        </SheetHeader>

        {/* Contents */}
        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-6">
          <p className="text-muted-foreground mb-3 px-2 font-serif text-[0.7rem] tracking-[0.35em] uppercase">
            Contents
          </p>
          <ol className="marker:content-none">
            {units.map((unit) => (
              <ContentsRow
                key={unitSegment(unit)}
                bookSlug={book.slug}
                unit={unit}
              />
            ))}
          </ol>
        </div>

        {firstLocked?.kind === "chapter" && (
          <div className="border-border shrink-0 border-t px-7 py-5">
            <UnlockLine book={book} edition={edition} chapter={firstLocked} />
            <BundleUnlockLine
              book={book}
              next={unitPath(book.slug, firstLocked)}
            />
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

/** A cover that is drawn but not yet retold: shelved, dimmed, not openable. */
function ForthcomingTile({
  edition,
  detail = "Forthcoming",
  eager = false,
}: {
  /**
   * A `ShelfEdition` satisfies this shape too, deliberately: a volume whose
   * chapters aren't in the catalog yet shelves exactly like a book that has
   * no catalog entry at all.
   */
  edition: ForthcomingEdition;
  detail?: string;
  eager?: boolean;
}) {
  return (
    <div className="group block">
      {/*
        Dimming means something different on each ground. Fading toward white
        keeps a cover legible as an object; fading toward the near-black dark
        background erases it — measured against the Frankenstein art, 45%
        opacity over the dark background lands the whole tile within 1.02:1 of
        the page, which reads as a broken image rather than a dimmed one. So
        dark theme keeps more of the art and, since a dark cover's edges still
        vanish into a dark page whatever the opacity, draws its own edge:
        `muted-foreground/60` is 3.33:1 there, enough to be seen as deliberate.
      */}
      <Cover
        src={edition.cover}
        size={edition.coverSize}
        alt={`Cover — ${edition.coverTitle}`}
        eager={eager}
        className="dark:ring-muted-foreground/60 opacity-45 saturate-[0.35] dark:opacity-70 dark:ring-1 dark:saturate-[0.55]"
      />
      <ShelfLine />
      <CoverCaption title={edition.coverTitle} detail={detail} muted />
    </div>
  );
}

/**
 * Caption for a cover the catalog holds no chapters for. A volume's own
 * caption would otherwise still promise its chapter range behind a cover that
 * opens onto an empty contents list — so it names the parent work and says
 * plainly that the chapters aren't here yet.
 */
function forthcomingDetail(book: CatalogBook, edition: ShelfEdition): string {
  return edition.chapters ? `${book.title} · Forthcoming` : "Forthcoming";
}

/** Volume counts a shelf actually reaches, spelled rather than numeraled. */
const COUNT_WORD: Record<number, string> = {
  2: "Two",
  3: "Three",
  4: "Four",
  5: "Five",
  6: "Six",
};

/** "Three volumes" — the caption under a stacked set. */
function volumeCount(n: number): string {
  return `${COUNT_WORD[n] ?? String(n)} volumes`;
}

/**
 * Where the volume at `depth` stands relative to the one in front of it: how
 * far its boards rise above that cover, how far its edges are drawn inside it,
 * and how far it has faded toward the page's own ground.
 *
 * Each step back is a diminishing share of the same limit, so a six-volume set
 * settles into the room a three-volume set already takes rather than marching
 * further with every volume. Pixels, not percentages: the deck has to stay
 * legible in a 155px mobile column, and the insets are what guarantee
 * containment there, not the size of the offsets.
 */
function volumeBehind(depth: number): {
  peek: number;
  inset: number;
  fade: number;
} {
  const recede = 1 - 0.58 ** depth;
  return { peek: 14 * recede, inset: 13 * recede, fade: 1 - 0.3 * recede };
}

/**
 * How many volumes the finished set will hold, which is what the collapsed
 * stack draws — not how many are shelved today. `bookConfig`'s `volumes` run
 * ahead of the covers deliberately (Don Quixote is configured through eight and
 * has art for three), and the boards can honestly stand for the unshipped ones
 * because they are boards: anonymous slabs, carrying no title and no artwork.
 * A reader looking at the shelf sees how long the set will be.
 *
 * Never fewer than the volumes actually on the shelf. A book with no `volumes`
 * in its config — every single-volume title, and any set priced as one product
 * — falls back to what it has, so nothing loses a board it had drawn before.
 */
function plannedVolumes(slug: string, shelved: number): number {
  return Math.max(BOOK_CONFIGS[slug]?.volumes?.length ?? 0, shelved);
}

/**
 * How the volume at `depth` opens when the set is hovered: how far it rises
 * above its resting peek, and which way it leans.
 *
 * Unlike `volumeBehind`, this one does NOT converge — the whole point of the
 * fan is that the back of the deck moves further than the front, so a reader
 * can see how many volumes are there. The rise is a flat pixel per step (3, 4,
 * 5 …) and the lean alternates left/right, gaining a degree every PAIR of
 * volumes (1°, 1°, 2°, 2°, 3°, 3° …) so the two boards leaning opposite ways
 * open by the same amount rather than one always out-swinging its neighbour.
 *
 * Both are small on purpose, and the lean especially. The boards pivot at the
 * bottom edge, where they stand on the shelf, so a lean of θ swings the TOP
 * corner out by roughly the cover's full height × sin θ — on the widest
 * (tallest) desktop column one degree is already ~8px of travel out there.
 */
function volumeFan(depth: number): { rise: number; tilt: number } {
  return {
    rise: 2 + depth,
    // Odd depths lean left, even lean right; negating both flips which way the
    // deck opens without disturbing the pairing.
    tilt: (depth % 2 === 0 ? 1 : -1) * Math.ceil(depth / 2),
  };
}

/**
 * A set of volumes as ONE object on the shelf: the front volume square to the
 * reader, the rest standing behind it and showing only their top boards.
 *
 * The depth is real — a perspective container and a lean of a few degrees —
 * but anchored at the BOTTOM edge, so the front cover stands on the shelf line
 * at full width the way every flat cover does and only its top edge draws back
 * (~5px lower, under 1% narrower). A stack turned about its centre instead
 * pushes one corner through the hairline and floats the other above it, which
 * reads as a book that has come off the shelf rather than one standing on it.
 *
 * The volumes behind are drawn INSIDE the front cover's box — inset from both
 * edges, rising above its top — rather than fanned out to one side. At rest
 * nothing they paint can reach the column gutter or the neighbouring cover at
 * any column width, and most of what rises above the cover is the room the lean
 * itself opened at the top of the cell.
 *
 * On HOVER the deck fans (`volumeFan`), and at the front of the stack the lean
 * just barely reaches past the cover's side edge. What it reaches into is the
 * column gutter (32px mobile / 40px desktop, so 20px is this cell's half at the
 * widest desktop column), and it is nowhere near spending it: in a 293px column
 * board one clears the cover edge by ~3px and board two lands flush with it.
 *
 * Note which board that is. The widest swing belongs to the SHALLOWEST board,
 * not the deepest, because `volumeBehind`'s inset grows faster over the first
 * steps than `volumeFan`'s lean does — the deeper boards are pulled in faster
 * than they are tipped out. It also means the first pair is not silhouette-
 * symmetric even though its two leans are equal and opposite: board one shows
 * ~3px more of itself on the left than board two shows on the right.
 *
 * The headroom is finite and is spent by DEPTH, since the lean gains a degree
 * every second volume while the inset paying for it has flattened out. Because
 * the stack now counts PLANNED volumes (`plannedVolumes`), the deepest board is
 * no longer the deepest shelved one: Don Quixote draws eight, so board seven
 * leans 4° and clears the cover edge by ~20px — the whole of this cell's half
 * of the desktop gutter. Two such sets side by side would fan to meet exactly
 * at the column midline. That is the ceiling, and the next configured volume
 * crosses it; clamp `volumeFan`'s magnitude at 3° before a ninth is added.
 * (Mobile is unaffected — the swing scales with the cover's height, so a 155px
 * column spends ~5px of its 16px.)
 *
 * The lean drops one BOTTOM corner as it lifts the opposite one, but at these
 * angles the drop (~2px at the front, ~7px at the back) is covered by the rise
 * that board is making anyway, so no corner dips below the front cover's own
 * bottom edge and nothing approaches the shelf hairline.
 *
 * They are boards, not artwork: a few pixels of a cover image is a random band
 * of colour that reads as a broken image on light art (the Quixote engravings),
 * where a board-toned slab carrying the cover's own radius and ring, with a
 * catch-light along its top edge, reads as a bound volume on either ground.
 * `muted-foreground` is mid-tone in both themes and the ground is at either
 * extreme, so one tone serves light and dark. It also means a set fetches one
 * image rather than one per volume.
 *
 * Being anonymous is also what lets the boards outnumber the editions. Only the
 * FRONT of the stack is a real volume — `sinkRead` has already sunk the finished
 * ones to the end of `editions`, so the volume the reader is up to is the one
 * facing out. Everything behind it is a count, not a cast: `behind` is a length,
 * and the boards are keyed on their depth rather than on any edition id.
 *
 * The front cover is the only one in normal flow: it sets the cell's height, and
 * the boards are absolutely positioned against it, painted deepest-first because
 * nothing but DOM order sorts them.
 */
function SeriesStack({
  editions,
  eager,
}: {
  editions: readonly ShelvedEdition[];
  eager: boolean;
}) {
  const [front] = editions;
  // `groupIntoSeries` never yields an empty set; drawing nothing beats throwing
  // on the whole shelf if that ever changes.
  if (!front) return null;
  const behind = plannedVolumes(front.edition.bookSlug, editions.length) - 1;

  return (
    <span className="relative block [perspective:1400px]">
      <span className="relative block [transform-origin:bottom] [transform:rotateX(3.5deg)] transition-transform duration-300 ease-out motion-safe:group-hover:-translate-y-1.5 motion-reduce:transition-none">
        {/*
          The hover fan is `motion-safe:`, not a `motion-reduce:`-disabled
          transition: dropping only the tween would still SNAP the deck open as
          the pointer crossed it, which is movement a reader who asked for none
          did not ask for. Gated this way the depth survives and the stack
          simply holds still — the same trade the single covers make with their
          lift, which the whole stack shares above.

          Each board carries its own rise and lean (`volumeFan`) rather than the
          container translating them as one block, which is what makes this a
          fan and not a lift. Per-board values can't ride on a utility class —
          every board would need its own — so they travel as custom properties
          in `style` and one shared class spends them on hover.
        */}
        <span
          aria-hidden="true"
          className="pointer-events-none absolute inset-0"
        >
          {Array.from({ length: behind }, (_, i) => ({
            depth: i + 1,
            ...volumeBehind(i + 1),
            ...volumeFan(i + 1),
          }))
            .reverse()
            .map(({ depth, peek, inset, fade, rise, tilt }) => (
              <span
                key={depth}
                className={cn(
                  "absolute rounded-[3px] rounded-l-[2px] bg-muted-foreground/40 bg-[linear-gradient(to_bottom,#ffffff59,#ffffff00_3px)] ring-1 ring-black/20 ring-inset dark:ring-white/15",
                  // Pivot at the shelf line, so the deck opens upward and no
                  // board's bottom corner swings below the cover it stands with.
                  "[transform-origin:bottom] transition-transform duration-300 ease-out motion-reduce:transition-none",
                  "motion-safe:group-hover:[transform:translateY(var(--fan-rise))_rotate(var(--fan-tilt))]",
                )}
                style={
                  {
                    top: -peek,
                    right: inset,
                    bottom: 0,
                    left: inset,
                    opacity: fade,
                    // Negated here rather than in `volumeFan`, so that function
                    // can read in the direction the board actually travels.
                    "--fan-rise": `${String(-rise)}px`,
                    "--fan-tilt": `${String(tilt)}deg`,
                  } as CSSProperties
                }
              />
            ))}
        </span>
        <Cover
          src={front.edition.cover}
          size={front.edition.coverSize}
          alt=""
          eager={eager}
          className="group-hover:shadow-[0_22px_34px_-16px_rgba(0,0,0,0.6),0_4px_10px_-4px_rgba(0,0,0,0.45)]"
        />
      </span>
    </span>
  );
}

/**
 * The closed set: the stack, and the control that opens it into its volumes.
 * Pressing it REPLACES this cell with `SeriesCase` — which is why
 * `LibraryContent` moves focus across the swap rather than leaving it on a
 * button that has just left the document.
 *
 * Carries no `aria-expanded`/`aria-controls`, deliberately. Those describe a
 * disclosure: a trigger that stays put beside the region it shows. This trigger
 * is GONE once the set is open, so `aria-expanded` could only ever be read as
 * `false` and `aria-controls` would point, while collapsed, at an id nothing in
 * the document carries. Claiming a pattern the markup does not implement is
 * worse than not claiming one — what a reader gets instead is a button that
 * says what it does, and a labelled region that receives focus when it opens.
 */
function SeriesTile({
  series,
  editions,
  readCount,
  onOpen,
  buttonRef,
  eager,
}: {
  series: ShelvedSeries;
  editions: readonly ShelvedEdition[];
  readCount: number;
  onOpen: () => void;
  buttonRef: (node: HTMLElement | null) => void;
  eager: boolean;
}) {
  const { book } = series;
  const language = LANGUAGE_LABEL[book.sourceLanguage] ?? book.sourceLanguage;
  return (
    <button
      type="button"
      ref={buttonRef}
      onClick={onOpen}
      className="group focus-visible:ring-ring/60 focus-visible:ring-offset-background block w-full cursor-pointer rounded-sm text-left focus-visible:ring-2 focus-visible:ring-offset-4 focus-visible:outline-none"
    >
      <SeriesStack editions={editions} eager={eager} />
      <ShelfLine />
      <CoverCaption
        title={book.title}
        detail={`${language} · ${volumeCount(editions.length)}`}
        read={readCount === editions.length}
      />
      <span className="text-muted-foreground group-hover:text-primary mt-2 inline-flex items-center gap-1.5 font-serif text-[0.7rem] tracking-[0.18em] uppercase transition-colors">
        <Layers aria-hidden="true" className="size-3.5" />
        {readCount > 0 && readCount < editions.length
          ? `${String(readCount)} of ${String(editions.length)} read`
          : "See volumes"}
      </span>
    </button>
  );
}

/**
 * The opened set: a full-width case holding the volumes as ordinary cells,
 * which the grid reflows around. Distinct ground and a border of its own, so
 * the volumes inside read as the CONTENTS of one object rather than as more
 * books on the same shelf.
 *
 * Reads `series.editions` itself rather than taking an editions prop: an open
 * case ALWAYS shows ascending volume order, and the one bug this component has
 * had was a call site handing it the read-sunk display order instead. No
 * separate prop, no way to diverge.
 */
function SeriesCase({
  series,
  isRead,
  onClose,
  caseRef,
}: {
  series: ShelvedSeries;
  isRead: (entry: ShelvedEdition) => boolean;
  onClose: () => void;
  caseRef: (node: HTMLElement | null) => void;
}) {
  const { book, editions } = series;
  const language = LANGUAGE_LABEL[book.sourceLanguage] ?? book.sourceLanguage;
  return (
    <section
      ref={caseRef}
      // Focused programmatically when the set opens (the button that opened it
      // is gone), so it needs to be focusable without being in the tab order.
      tabIndex={-1}
      aria-label={`${book.title} — ${volumeCount(editions.length).toLowerCase()}`}
      className="animate-case-open border-border bg-muted/40 ring-border/40 focus-visible:ring-ring/60 rounded-xl border px-5 py-6 ring-1 ring-inset focus-visible:ring-2 focus-visible:outline-none sm:px-7 sm:py-7"
    >
      <header className="border-border/70 mb-7 flex flex-wrap items-end justify-between gap-x-6 gap-y-3 border-b pb-4">
        <div>
          <p className="text-muted-foreground font-serif text-[0.7rem] tracking-[0.3em] uppercase">
            {language} · {volumeCount(editions.length)}
          </p>
          <h2 className="font-display text-foreground mt-1.5 text-2xl leading-tight font-black tracking-tight text-balance">
            {book.title}
          </h2>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="text-muted-foreground hover:text-primary focus-visible:ring-ring/60 inline-flex items-center gap-1.5 rounded-sm font-serif text-[0.7rem] tracking-[0.18em] uppercase transition-colors focus-visible:ring-2 focus-visible:outline-none"
        >
          <X aria-hidden="true" className="size-3.5" />
          Close set
        </button>
      </header>
      <ul className="grid grid-cols-2 gap-x-8 gap-y-12 sm:grid-cols-3 sm:gap-x-10">
        {editions.map(({ edition, book: editionBook, units }) => (
          <li key={edition.id}>
            {units.length > 0 ? (
              <EditionTile
                book={editionBook}
                edition={edition}
                units={units}
                eager={false}
                read={isRead({ edition, book: editionBook, units })}
              />
            ) : (
              <ForthcomingTile
                edition={edition}
                detail={forthcomingDetail(editionBook, edition)}
              />
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}

/**
 * Focus follow-through for a disclosure whose trigger is REPLACED by what it
 * opens (and vice versa). Without it, a keyboard reader loses focus to the
 * document body every time they open or close a set.
 *
 * `flushSync` rather than an effect, deliberately: the node to focus does not
 * exist until the state change has rendered, and the honest way to say "render
 * this now, then touch the DOM" is to say it — an effect that fired afterwards
 * would have to carry a pending-key state and clear it on the next render, which
 * is a cascade React tells you not to write (react-useeffect-discipline).
 */
function useSwapFocus(): {
  register: (key: string) => (node: HTMLElement | null) => void;
  swapFocusTo: (key: string, update: () => void) => void;
} {
  const nodes = useRef(new Map<string, HTMLElement | null>());
  return {
    register: (key) => (node) => {
      nodes.current.set(key, node);
    },
    swapFocusTo: (key, update) => {
      flushSync(update);
      nodes.current.get(key)?.focus();
    },
  };
}

/**
 * The shelf itself. Mounted from the FIRST render, before the order it will be
 * drawn in is known, because the corpus fetch starts here: `useCatalog()` is
 * what calls `fetchCatalog()`, so a parent that withheld this component until
 * Convex answered would not merely delay the shelf — it would delay the CDN
 * request, turning two round trips that overlap into two that queue. The wait
 * for the order therefore happens BELOW the fetch, not above it.
 */
function LibraryContent({
  bookOrder,
  marks,
  ordered,
}: {
  bookOrder: readonly string[] | null;
  marks: ReadMarks;
  /** Have both order inputs settled? See `LibraryPage` for why we wait. */
  ordered: boolean;
}) {
  const catalog = useCatalog();
  const [opened, setOpened] = useState<readonly string[]>([]);
  const { register, swapFocusTo } = useSwapFocus();

  // Every hook above runs unconditionally; this is the order gate, and it sits
  // here rather than in `LibraryPage` so that suspending on the catalog and
  // waiting on Convex happen CONCURRENTLY. The skeleton is the same one the
  // Suspense fallback shows, so the reader sees one continuous loading state
  // whichever half lands last.
  if (!ordered) return <LibrarySkeleton />;

  // Shelved a book at a time, not a cover at a time: which volume of the
  // Odyssey binds the foreword depends on the other two (see `shelveCatalog`).
  // `bookOrder` is the admin-set shelf order (`convex/shelf.ts`), settled by
  // the time we get past the gate above; `null` means none was ever saved, and
  // the order shipped in `shelf.ts` stands.
  const series = groupIntoSeries(shelveCatalog(catalog.books, bookOrder));

  const isRead = (entry: ShelvedEdition): boolean =>
    marks.isRead(entry.edition.bookSlug, lastChapterOf(entry.units));

  return (
    <ul className="grid grid-cols-2 gap-x-8 gap-y-14 sm:grid-cols-3 sm:gap-x-10">
      {series.map((set, i) => {
        // Finished volumes sink to the back of the CLOSED stack — see
        // `sinkRead`. That order is purely visual depth: it decides which
        // cover faces out on the collapsed tile, nothing more. The opened
        // case renders `set.editions` instead, so a set always reads in
        // ascending volume order once it's open, whatever's been read.
        const editions = sinkRead(set.editions, isRead);
        const single = editions[0];
        const eager = i < 3;

        // A one-volume book has no set to open: it shelves exactly as it did
        // before series existed.
        if (editions.length === 1 && single) {
          return (
            <li key={set.bookSlug}>
              {/*
                A volume the catalog holds no chapters for shelves as
                forthcoming rather than as an openable cover: build-corpus skips
                premium chapters that aren't seeded yet (and a `--local` build
                without the rewrites authored skips them all), so this is a
                supported state, not a build failure. Opening it would show an
                empty contents list and an unlock link pointing at a chapter
                that isn't in the catalog — which the reader route answers with
                "That chapter could not be found". Better to shelve it honestly
                than to sell a dead link.
              */}
              {single.units.length > 0 ? (
                <EditionTile
                  book={single.book}
                  edition={single.edition}
                  units={single.units}
                  eager={eager}
                  read={isRead(single)}
                />
              ) : (
                <ForthcomingTile
                  edition={single.edition}
                  detail={forthcomingDetail(single.book, single.edition)}
                  eager={eager}
                />
              )}
            </li>
          );
        }

        return opened.includes(set.bookSlug) ? (
          <li key={set.bookSlug} className="col-span-full">
            <SeriesCase
              series={set}
              isRead={isRead}
              caseRef={register(`case:${set.bookSlug}`)}
              onClose={() => {
                swapFocusTo(`tile:${set.bookSlug}`, () => {
                  setOpened(opened.filter((slug) => slug !== set.bookSlug));
                });
              }}
            />
          </li>
        ) : (
          <li key={set.bookSlug}>
            <SeriesTile
              series={set}
              editions={editions}
              readCount={editions.filter(isRead).length}
              buttonRef={register(`tile:${set.bookSlug}`)}
              eager={eager}
              onOpen={() => {
                swapFocusTo(`case:${set.bookSlug}`, () => {
                  setOpened([...opened, set.bookSlug]);
                });
              }}
            />
          </li>
        );
      })}
      {FORTHCOMING.map((edition) => (
        <li key={edition.id}>
          <ForthcomingTile edition={edition} />
        </li>
      ))}
    </ul>
  );
}

function LibrarySkeleton() {
  return (
    <div className="grid grid-cols-2 gap-x-8 gap-y-14 sm:grid-cols-3 sm:gap-x-10">
      {Array.from({ length: 6 }, (_, i) => (
        <div key={i}>
          <Skeleton className="aspect-[788/1239] w-full rounded-[3px]" />
          <div className="bg-border mt-4 h-px w-full" />
          <Skeleton className="mt-4 h-5 w-3/4" />
          <Skeleton className="mt-2 h-3 w-1/2" />
        </div>
      ))}
    </div>
  );
}

export function LibraryPage() {
  // BOTH of these decide the ORDER the shelf is drawn in, so both are hoisted
  // above the Suspense boundary AND waited on inside it.
  //
  // Hoisted because `LibraryContent` suspends on the corpus fetch: a query
  // subscribed inside it would not even start until that resolved, serializing
  // two round trips that can perfectly well overlap. Up here they are in flight
  // alongside the catalog.
  //
  // Waited on because being in flight together is not the same as arriving
  // together. Rendering the shelf against a provisional value only to revise it
  // is a visible snap — the covers rearrange under a reader who is already
  // looking at them — and "the query almost always wins" is not a guarantee.
  // So the shelf paints once, from settled values, or not yet at all.
  //
  // The wait is passed DOWN as `ordered` rather than swapping this subtree out
  // for a skeleton, because `LibraryContent` is what calls `fetchCatalog()`:
  // not rendering it would hold the CDN request behind the Convex round trip
  // and undo the hoisting above. It renders, it suspends, and the order gate
  // sits under the fetch it must not block.
  //
  // The cost is honest: if Convex is slow or unreachable while the static
  // catalog is fine, the skeleton stays up longer than it used to instead of
  // painting an order that may be wrong. Deterministic beats probabilistic.
  //
  // `undefined` here means "still loading" — distinct from `null`, which is a
  // loaded answer meaning no admin order was ever saved, and which
  // `shelveCatalog` correctly reads as "ship the default".
  //
  // Held (`useLastLoaded`) so the wait is FIRST-load-only: if the subscription
  // ever restarts (an auth transition re-runs Convex queries), the gate must
  // not drop a shelf the reader is using back to skeleton. The marks half
  // latches the same way inside `useReadMarks`.
  const bookOrder = useLastLoaded(useQuery(api.shelf.getOrder));
  // Read volumes sink to the back of their set, so an unsettled marks store
  // would re-sort a set the same way. `marks.settled` covers the signed-in
  // reader's cross-device marks; the local half is synchronous either way.
  const marks = useReadMarks();
  return (
    <div className="bg-background min-h-svh">
      <main className="mx-auto max-w-5xl px-6 pt-20 pb-16 sm:px-8">
        {/* Masthead — the volume's title page. */}
        <header className="mb-16 text-center">
          <p className="text-muted-foreground font-serif text-sm tracking-[0.35em] uppercase">
            Read side by side
          </p>
          <h1 className="font-display text-foreground mt-3 text-6xl font-black tracking-tight sm:text-7xl">
            Retells
          </h1>
          <p className="text-muted-foreground mx-auto mt-5 max-w-xl font-serif text-lg leading-relaxed text-pretty">
            Classic works retold in plain, modern prose — each retelling synced
            line-for-line against its original.
          </p>
          {/* The masthead's one outbound link: the footer carries an About
              link too, but it sits below the whole shelf; the story behind
              the site deserves a line a reader actually sees. */}
          <Link
            to="/about"
            className="text-muted-foreground hover:text-primary mt-6 inline-block font-serif text-xs tracking-[0.2em] uppercase transition-colors"
          >
            The story behind the shelf&nbsp;→
          </Link>
          <div
            aria-hidden="true"
            className="border-border mx-auto mt-10 w-24 border-t"
          />
        </header>

        <ErrorBoundary
          fallback={(error, reset) => (
            <div
              role="alert"
              className="border-destructive/40 bg-destructive/5 rounded-lg border p-6"
            >
              <p className="text-foreground font-serif">
                The library could not be loaded.
              </p>
              <p className="text-muted-foreground mt-1 font-serif text-sm">
                {error.message}
              </p>
              <button
                type="button"
                // Drop the cached REJECTION before re-rendering, or this button
                // would rethrow the same settled promise and change nothing — a
                // corpus fetch is only retried when a reader asks (see
                // `retryCorpusFetch`).
                onClick={() => {
                  retryCorpusFetch();
                  reset();
                }}
                className="text-primary mt-4 font-serif text-sm underline underline-offset-4"
              >
                Try again
              </button>
            </div>
          )}
        >
          <Suspense fallback={<LibrarySkeleton />}>
            <LibraryContent
              bookOrder={bookOrder ?? null}
              marks={marks}
              ordered={bookOrder !== undefined && marks.settled}
            />
          </Suspense>
        </ErrorBoundary>
      </main>

      <footer className="mx-auto max-w-5xl px-6 pb-12 sm:px-8">
        <div className="border-border border-t pt-6 text-center">
          <Link
            to="/about"
            className="text-muted-foreground hover:text-primary font-serif text-xs tracking-[0.2em] uppercase transition-colors"
          >
            About Retells
          </Link>
          <p className="text-muted-foreground mt-3 font-serif text-xs">
            Original texts are in the public domain. Retellings are original
            works — please read the copyright notice within each book.
          </p>
        </div>
      </footer>
    </div>
  );
}
